/**
 * IndexedDB-backed PDF cache.
 *
 * Downloads a PDF from a URL, stores the ArrayBuffer in IndexedDB keyed by URL,
 * and returns a File object. Re-fetches if the cached entry is stale (>24 h).
 */

const DB_NAME = 'pressstr-pdf-cache';
const STORE_NAME = 'pdfs';
const DB_VERSION = 1;
const MAX_AGE_MS = 24 * 60 * 60 * 1000; // 24 hours

interface CachedEntry {
  url: string;
  buffer: ArrayBuffer;
  cachedAt: number;
  filename: string;
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      req.result.createObjectStore(STORE_NAME, { keyPath: 'url' });
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function getCached(url: string): Promise<CachedEntry | null> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readonly');
    const req = tx.objectStore(STORE_NAME).get(url);
    req.onsuccess = () => resolve(req.result ?? null);
    req.onerror = () => reject(req.error);
  });
}

async function putCached(entry: CachedEntry): Promise<void> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const req = tx.objectStore(STORE_NAME).put(entry);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}

export type DownloadProgressCallback = (loaded: number, total: number) => void;

/**
 * Download a PDF from `url`, cache it in IndexedDB, and return it as a File.
 * Uses the cache if present and fresh (< 24 h).
 * @param url       The PDF URL to fetch
 * @param onProgress Called with (loaded, total) during download; total may be 0 if unknown
 */
export async function fetchPdfCached(
  url: string,
  onProgress?: DownloadProgressCallback,
): Promise<File> {
  // Check cache first
  const cached = await getCached(url);
  if (cached && Date.now() - cached.cachedAt < MAX_AGE_MS) {
    return new File([cached.buffer], cached.filename, { type: 'application/pdf' });
  }

  // Fetch with progress via XHR
  const buffer = await new Promise<ArrayBuffer>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('GET', url, true);
    xhr.responseType = 'arraybuffer';
    xhr.onprogress = (e) => {
      onProgress?.(e.loaded, e.total);
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(xhr.response as ArrayBuffer);
      } else {
        reject(new Error(`HTTP ${xhr.status} — ${xhr.statusText}`));
      }
    };
    xhr.onerror = () => reject(new Error('Network error while downloading PDF'));
    xhr.send();
  });

  const filename = url.split('/').pop()?.split('?')[0] ?? 'document.pdf';

  // Store in IndexedDB
  await putCached({ url, buffer, cachedAt: Date.now(), filename });

  return new File([buffer], filename, { type: 'application/pdf' });
}
